import {
  BaseAtomSpec,
  generateAtomContainerSchema,
} from "components/cards/atoms/BaseAtomSpec";
import {
  generateBooleanSchema,
  generateColorSchema,
  generateNumberSchema,
  generateRootObjectSchema,
  generateStringSchema,
} from "components/cards/common/schema";

import {
  TextInputParticleSpec,
  TextParticleSpec,
  ButtonParticleSpec,
} from "components/cards/particles/YinzCam";

const defaultHeadingValues = {
  size: "2.5rem",
  weight: "800",
  transform: "uppercase",
  align: "left",
  wrap: "nowrap",
  lineHeight: "1.0",
  container: {
    fillWidth: true,
    fillHeight: true,
    padding: "8px 15px",
    background: { visible: true, color: "#0c2237" },
  },
};

export const specification = new BaseAtomSpec(
  "Email Submit Atom",
  generateRootObjectSchema({
    container: generateAtomContainerSchema({
      defaultValues: {
        background: { visible: true, color: "#FFFFFF" },
      },
    }),
    rowHeight: generateStringSchema(
      "Row Height",
      "The height of each row in standard CSS units.",
      {
        defaultValue: "4.5rem",
      },
    ),
    submitTransitionDuration: generateStringSchema(
      "Layout Animation Transition Duration (in ms)",
      "Duration of the submit animation and segment resizing in milliseconds.",
      { defaultValue: "1000" },
    ),
    textFadeTransitionDuration: generateStringSchema(
      "Text Animation Transition Duration (in ms)",
      "Duration for fading from pre-submit text to post-submit text in milliseconds.",
      { defaultValue: "1000" },
    ),
    invalidEmailText: generateStringSchema(
      "Invalid Email Text",
      "Text shown when email validation fails.",
      {
        defaultValue: "ENTER A VALID EMAIL",
      },
    ),
    beforeSubmitText: TextParticleSpec.generateSchema(
      "Text Before Submit",
      "Text shown before the submit action.",
      {
        defaultValues: {
          ...defaultHeadingValues,
          text: "KEEP UPDATED",
          color: "#ffffff",
          align: "left",
        },
      },
    ),
    afterSubmitText: TextParticleSpec.generateSchema(
      "Text After Submit",
      "Text shown after a successful submit action.",
      {
        defaultValues: {
          ...defaultHeadingValues,
          text: "YOU'RE ALL SIGNED UP",
          color: "#ffffff",
        },
      },
    ),
    emailInput: TextInputParticleSpec.generateSchema(
      "Email Input",
      "Email input field settings and placeholder styling.",
      {
        defaultValues: {
          container: {
            fillWidth: true,
            fillHeight: true,
            padding: "0 1rem",
            border: { visible: false },
          },
          backgroundColor: "transparent",
          text: {
            text: "EMAIL ADDRESS",
            size: "2.5rem",
            weight: "700",
            transform: "uppercase",
            color: "#D4D4D4",
            wrap: "nowrap",
          },
          image: {
            source:
              "https://yc-app-resources.s3.us-east-1.amazonaws.com/pjl/icons/Alert.png",
            alt: "Alert icon",
            container: { fillHeight: false, fillWidth: true, width: "1.5rem" },
            sizing: "cover",
          },
          showImage: true,
        },
      },
    ),
    emailPlaceholderMobile: generateStringSchema(
      "Mobile Email Placeholder Override",
      "Optional placeholder override for mobile only. Leave blank to use Email Input text.",
      { defaultValue: "EMAIL" },
    ),
    submitButton: ButtonParticleSpec.generateSchema(
      "Submit Button",
      "Button shown before submit.",
      {
        defaultValues: {
          showText: true,
          text: {
            text: "SUBMIT",
            size: "2rem",
            weight: "700",
            transform: "uppercase",
            color: "#1D110C",
            align: "center",
            wrap: "nowrap",
          },
          gap: "1.5rem",
          showImage: true,
          imagePosition: "after",
          image: {
            source:
              "https://yc-app-resources.s3.us-east-1.amazonaws.com/pjl/icons/rightArrow.png",
            alt: "Submit button right arrow",
            container: { fillHeight: false, fillWidth: true, width: "1.5rem" },
            sizing: "cover",
            fitHorizontalAlign: "center",
            fitVerticalAlign: "center",
          },
          justification: "center",
          container: {
            fillWidth: true,
            fillHeight: true,
            centerContentHorizontally: true,
            centerContentVertically: true,
            background: { visible: true, color: "#FF4800" },
            padding: "0 1rem",
          },
        },
      },
    ),
    buttonTextMobile: generateStringSchema(
      "Mobile Button Text Override",
      "Optional text override for the submit button on mobile only. Leave blank to use Submit Button text.",
      { defaultValue: "Submit" },
    ),
    showSubmitButtonImageOnMobile: generateBooleanSchema(
      "Show Submit Button inner Image on Mobile",
      "Whether the submit button image should be shown on mobile.",
      { defaultValue: false },
    ),
    desktopBeforeLeadingWidth: generateNumberSchema(
      "Desktop Before Width - Segment 1",
      "Desktop width percentage for the leading placeholder before submit.",
      { defaultValue: 12 },
    ),
    desktopBeforeTextWidth: generateNumberSchema(
      "Desktop Before Width - Segment 2",
      "Desktop width percentage for the text segment before submit.",
      { defaultValue: 32 },
    ),
    desktopBeforeInputWidth: generateNumberSchema(
      "Desktop Before Width - Segment 3",
      "Desktop width percentage for the input segment before submit.",
      { defaultValue: 38 },
    ),
    desktopBeforeButtonWidth: generateNumberSchema(
      "Desktop Before Width - Segment 4",
      "Desktop width percentage for the button segment before submit.",
      { defaultValue: 14 },
    ),
    desktopBeforeTrailingWidth: generateNumberSchema(
      "Desktop Before Width - Segment 5",
      "Desktop width percentage for the trailing placeholder before submit.",
      { defaultValue: 4 },
    ),
    desktopAfterLeadingWidth: generateNumberSchema(
      "Desktop After Width - Segment 1",
      "Desktop width percentage for the leading placeholder after submit.",
      { defaultValue: 14 },
    ),
    desktopAfterTextWidth: generateNumberSchema(
      "Desktop After Width - Segment 2",
      "Desktop width percentage for the text segment after submit.",
      { defaultValue: 32 },
    ),
    desktopAfterInputWidth: generateNumberSchema(
      "Desktop After Width - Segment 3",
      "Desktop width percentage for the input segment after submit.",
      { defaultValue: 40 },
    ),
    desktopAfterCombinedWidth: generateNumberSchema(
      "Desktop After Width - Segment 4",
      "Desktop width percentage for the combined final segment after submit.",
      { defaultValue: 14 },
    ),
    mobileInputWidth: generateNumberSchema(
      "Mobile Input Width",
      "Width percentage for the input section in the mobile action row.",
      { defaultValue: 60 },
    ),
    mobileButtonWidth: generateNumberSchema(
      "Mobile Button Width",
      "Width percentage for the submit button section in the mobile action row.",
      { defaultValue: 40 },
    ),
    beforeLeadingBackgroundColor: generateColorSchema(
      "Before Submit - Segment 1 Color",
      "Background color for the leading placeholder before submit.",
      { defaultValue: "#0c2237" },
    ),
    beforeTrailingBackgroundColor: generateColorSchema(
      "Before Submit - Segment 5 Color",
      "Background color for the trailing placeholder before submit.",
      { defaultValue: "#0c2237" },
    ),
    afterLeadingBackgroundColor: generateColorSchema(
      "After Submit - Segment 1 Color",
      "Background color for the leading placeholder after submit.",
      { defaultValue: "#0c2237" },
    ),
    afterTextBackgroundColor: generateColorSchema(
      "After Submit - Segment 2 Color",
      "Background color for the text segment after submit.",
      { defaultValue: "#000000" },
    ),
    afterInputBackgroundColor: generateColorSchema(
      "After Submit - Segment 3 Color",
      "Background color for the input segment after submit.",
      { defaultValue: "#D0D0D0" },
    ),
    afterCombinedBackgroundColor: generateColorSchema(
      "After Submit - Segment 4 Color",
      "Background color for the combined final segment after submit.",
      { defaultValue: "#FFFFFF" },
    ),
  }),
);
